import React, { useMemo } from 'react';

import { FormattedMessage } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { FocusIcon } from '@atlaskit/logo';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives/compiled';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type SearchResultType as SearchClientResultType } from '@atlassian/search-client/common/types';

import { ProductKeys } from '../../../../../../common/constants/product-keys';
import type { SearchResultProps } from '../../../../../../common/types';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { getSiteMetadata } from '../../../../../../common/utils/sites';
import { getSummarizedAdfContent } from '../../../../../../common/utils/summarized-adf-content';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';

import type { searchMercuryFocusAreaUpdate_AtlassianSearchPage$key } from './__generated__/searchMercuryFocusAreaUpdate_AtlassianSearchPage.graphql';
import { messages } from './messages';

export const SearchMercuryFocusAreaUpdate = ({
	node,
	position,
	cloudId,
	searchQuery,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	mainContentWidth,
	selectedSites,
}: SearchResultProps<searchMercuryFocusAreaUpdate_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchMercuryFocusAreaUpdate_AtlassianSearchPage on SearchResult {
				__typename
				id
				description
				type
				url
				... on SearchResultMercuryFocusAreaStatusUpdate {
					lastModifiedDate
					focusArea {
						name
					}
				}
			}
		`,
		node,
	);

	const metadata = useMemo(() => {
		const [product, contentId] = getProductAndIDFromARI(data.id);
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: data.type,
			entityType: data.type,
			contentId,
			product,
			...getSiteMetadata(data.id, cloudId, selectedSites),
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data.id, data.type, cloudId, selectedSites]);

	const onResultClicked = useFireResultClicked({ metadata, position });
	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({ metadata, position });
	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const description = useMemo(
		() => getSummarizedAdfContent(data.description, searchQuery),
		[data.description, searchQuery],
	);

	const headerMetadataParts = useMemo(
		() => [
			[
				'type',
				<Box>
					<SearchResultType type={data.type as SearchClientResultType} />
				</Box>,
			],
			[
				'last-modified',
				data.lastModifiedDate ? (
					<Box>
						<LastModified lastModified={data.lastModifiedDate} />
					</Box>
				) : null,
			],
		],
		[data.type, data.lastModifiedDate],
	);

	return (
		<SearchResult
			{...data}
			product={ProductKeys.Focus}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={data.url}
			title={
				<FormattedMessage
					{...messages.statusUpdateTitle}
					values={{ focusAreaName: data.focusArea?.name }}
				/>
			}
			icon={<FocusIcon size="medium" shouldUseNewLogoDesign />}
			headerMetadataParts={headerMetadataParts}
			description={description}
			cloudId={cloudId}
			position={position}
			partial={data}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
			selectedSites={selectedSites}
		/>
	);
};

export const isSearchMercuryFocusAreaUpdate = (
	node?: any,
): node is searchMercuryFocusAreaUpdate_AtlassianSearchPage$key => {
	return (
		node?.id?.includes('mercury') && node?.__typename === 'SearchResultMercuryFocusAreaStatusUpdate'
	);
};
